import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Printer } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const CAT_LABEL = {
  starters: "Starters", mains: "Mains", sides: "Sides & breads",
  desserts: "Desserts", drinks_non_alc: "Drinks", drinks_alc: "Wine, beer & cocktails",
};

export default function PrintBooking() {
  const { bid } = useParams();
  const { user } = useAuth();
  const [b, setB] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/bookings/${bid}`);
        setB(data);
      } catch (e) { /* ignore */ }
    })();
  }, [bid]);

  if (!b) return <div className="p-10 text-light-ink">Loading…</div>;

  const items = b.curated_menu?.items || [];
  const byCat = items.reduce((acc, it) => { (acc[it.category] = acc[it.category] || []).push(it); return acc; }, {});
  const isKot = user?.role === "restaurant";

  return (
    <div className="min-h-screen bg-white text-ink p-8 print:p-2 max-w-3xl mx-auto" data-testid="print-page">
      <style>{`@media print { @page { margin: 14mm; } body { background: white !important; } .tablo-btn-primary, .tablo-btn-outline { display: none !important; } }`}</style>
      <div className="flex items-center justify-between print:hidden mb-6">
        <h1 className="font-serif text-2xl">{isKot ? "Kitchen Order Ticket" : "Booking summary"}</h1>
        <Button onClick={() => window.print()} className="tablo-btn-primary" data-testid="print-btn">
          <Printer className="w-4 h-4 mr-2" strokeWidth={1.6}/> Print / Save as PDF
        </Button>
      </div>

      <header className="border-b border-tablo-border pb-4 mb-4">
        <div className="flex items-baseline justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-amber font-medium">{isKot ? "KOT" : "TABLO BOOKING"}</p>
            <p className="font-serif text-3xl">{b.restaurant?.name}</p>
            <p className="text-xs text-light-ink mt-1">{b.restaurant?.area}{b.restaurant?.contact_phone ? ` · ${b.restaurant.contact_phone}` : ""}</p>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wider text-light-ink">Booking</p>
            <p className="font-mono text-xs">{b.id}</p>
            <p className="text-[10px] uppercase tracking-wider text-light-ink mt-1.5">Mode</p>
            <p className="text-xs font-medium capitalize">{(b.dining_kind || "").replace("_", "-") || "—"}</p>
          </div>
        </div>
      </header>

      <div className="grid grid-cols-4 gap-4 text-sm mb-6">
        <div><p className="text-[10px] uppercase text-light-ink">Date</p><p className="font-medium">{b.date}</p></div>
        <div><p className="text-[10px] uppercase text-light-ink">Time</p><p className="font-medium">{b.time}</p></div>
        <div><p className="text-[10px] uppercase text-light-ink">Pax</p><p className="font-medium">{b.pax}</p></div>
        <div><p className="text-[10px] uppercase text-light-ink">Status</p><p className="font-medium">{b.status.replace("_", " ")}</p></div>
      </div>

      {!isKot && (
        <div className="grid grid-cols-3 gap-4 text-sm mb-6 bg-cream rounded-xl p-4">
          <div><p className="text-[10px] uppercase text-light-ink">Deposit</p><p className="font-serif text-xl">₹{(b.deposit_amount_inr || 0).toLocaleString("en-IN")}</p></div>
          <div><p className="text-[10px] uppercase text-light-ink">Balance</p><p className="font-serif text-xl">₹{(b.balance_amount_inr || 0).toLocaleString("en-IN")}</p></div>
          <div><p className="text-[10px] uppercase text-light-ink">Total</p><p className="font-serif text-xl">₹{((b.deposit_amount_inr || 0) + (b.balance_amount_inr || 0)).toLocaleString("en-IN")}</p></div>
        </div>
      )}

      {b.package && (
        <div className="mb-6">
          <p className="text-[10px] uppercase tracking-wider text-amber font-medium">Package</p>
          <p className="font-serif text-lg">{b.package.name} · ₹{b.package.price_per_head_inr}/head</p>
          <p className="text-xs text-light-ink">{(b.package.inclusions || []).join(" · ")}</p>
        </div>
      )}

      <div className="space-y-4">
        <p className="text-[10px] uppercase tracking-[0.18em] text-amber font-medium">Dishes ordered</p>
        {Object.keys(byCat).length === 0 && <p className="text-light-ink text-sm">No menu selection yet.</p>}
        {Object.entries(byCat).map(([cat, dishes]) => (
          <div key={cat}>
            <p className="text-xs uppercase tracking-wider text-light-ink mb-1">{CAT_LABEL[cat] || cat}</p>
            <table className="w-full text-sm border-t border-tablo-border">
              <thead><tr className="text-[10px] uppercase text-light-ink"><th className="text-left py-1">Dish</th><th className="text-right py-1">{isKot ? "Portions" : "Qty"}</th></tr></thead>
              <tbody>
                {dishes.map((d, i) => (
                  <tr key={i} className="border-t border-tablo-border/60">
                    <td className="py-1.5"><span className={`inline-block w-2 h-2 rounded-full mr-2 ${d.veg_flag ? "bg-green-600" : "bg-rose-600"}`}/>{d.name}</td>
                    <td className="py-1.5 text-right font-medium">{d.qty != null ? d.qty : `× ${b.pax}`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      {/* Guest dietary briefing on the KOT */}
      {b.guest_preferences && (
        (b.guest_preferences.allergens?.length > 0
          || b.guest_preferences.veg_preference === "veg_only"
          || (b.guest_preferences.spice_level != null && b.guest_preferences.spice_level !== 2)
        ) && (
          <div className="mt-4 border border-rose-300 bg-rose-50 rounded-lg p-3 text-xs" data-testid="print-prefs-briefing">
            <p className="text-rose-800 font-bold mb-1">⚠ DIETARY BRIEFING</p>
            {b.guest_preferences.allergens?.length > 0 && (
              <p className="text-rose-700">• Allergens to avoid: <strong>{b.guest_preferences.allergens.join(", ")}</strong></p>
            )}
            {b.guest_preferences.veg_preference === "veg_only" && (
              <p className="text-rose-700">• Veg-only group — no non-veg dishes</p>
            )}
            {typeof b.guest_preferences.spice_level === "number" && (
              <p className="text-rose-700">• Spice tolerance: <strong>{["None", "Mild", "Medium", "Hot"][b.guest_preferences.spice_level]}</strong></p>
            )}
          </div>
        )
      )}

      <footer className="mt-8 pt-4 border-t border-tablo-border text-xs text-light-ink">
        <div className="flex justify-between">
          <span>Organizer: {b.organizer?.name || "—"}{b.organizer?.tablo_contact ? ` · Tablo Ops: ${b.organizer.tablo_contact}` : (b.organizer?.phone ? ` · ${b.organizer.phone}` : "")}</span>
          <span>Printed {new Date().toLocaleString("en-IN")}</span>
        </div>
        {isKot && <p className="mt-2 italic">Prepare only after guest check-in. Chef sets portion sizes for {b.pax} guests.</p>}
      </footer>
    </div>
  );
}
